import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    unitType: '3-bedroom'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Thank you for your inquiry! We will contact you shortly.');
    setFormData({ name: '', email: '', phone: '', message: '', unitType: '3-bedroom' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wider uppercase text-sm">
            Get In Touch
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-slate-900 font-medium mt-4 mb-6">
            Schedule a Visit
          </h2>
          <p className="text-slate-500 text-lg">
            Ready to experience luxury living? Contact us today to schedule 
            a private viewing.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h3 className="font-serif text-2xl text-slate-900 font-medium mb-8">
                Contact Information
              </h3>

              <div className="space-y-6">
                <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <Phone className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Phone</p>
                    <p className="text-slate-900 font-medium">{contactInfo.phone}</p>
                  </div>
                </a>

                <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Email</p>
                    <p className="text-slate-900 font-medium text-sm">{contactInfo.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Address</p>
                    <p className="text-slate-900 font-medium">{contactInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100">
                <p className="text-slate-400 text-sm mb-4">Follow Us</p>
                <div className="flex gap-3">
                  <a
                    href="https://www.instagram.com/concord_apartments"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-amber-600 transition-colors text-sm font-medium"
                  >
                    Instagram
                  </a>
                  <span className="text-slate-300">•</span>
                  <a
                    href="https://www.facebook.com/concordapartments"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-amber-600 transition-colors text-sm font-medium"
                  >
                    Facebook
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Features */}
            <div className="space-y-3">
              {['Flexible payment plans', 'Professional property tour', 'Investment consultation'].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  <span className="text-slate-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h3 className="font-serif text-2xl text-slate-900 font-medium mb-8">
                Request Information
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="rounded-xl border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="rounded-xl border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+256 700 000 000"
                    className="rounded-xl border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Interested Unit
                  </label>
                  <select
                    name="unitType"
                    value={formData.unitType}
                    onChange={handleChange}
                    className="w-full h-10 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-amber-500 px-3 text-sm bg-white"
                  >
                    <option value="3-bedroom">3 Bedroom - $330,000</option>
                    <option value="3+1-bedroom">3+1 Bedroom - $350,000</option>
                    <option value="penthouse">Penthouse - $850,000</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    className="rounded-xl border-slate-200 focus:border-amber-500 focus:ring-amber-500 resize-none"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-6 text-base font-semibold transition-all duration-300 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Inquiry
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
